import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, LogIn, UserPlus, User } from 'lucide-react';
import PublicHeader from './PublicHeader';
import './Auth.css';

const Auth = () => {
  const navigate = useNavigate();
  const [isLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, signup, loginAnonymously } = useAuth();

  useEffect(() => {
    document.title = 'Squeekit - Sign In';
  }, []);

  // Access code for account creation
  const VALID_ACCESS_CODE = 'WQM-2025-X7K9-M3N8-P2Q5';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isLogin) {
      // Validate access code for signup
      if (accessCode !== VALID_ACCESS_CODE) {
        return setError('Invalid access code. Please contact administrator for access.');
      }
      
      if (password !== confirmPassword) {
        return setError('Passwords do not match');
      }
    }

    try {
      setLoading(true);
      if (isLogin) {
        await login(email, password);
        navigate('/', { replace: true });
      } else {
        await signup(email, password);
        navigate('/', { replace: true });
      }
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleAnonymousLogin = async () => {
    setError('');

    // Validate access code for anonymous login
    if (accessCode !== VALID_ACCESS_CODE) {
      return setError('Invalid access code. Please contact administrator for access.');
    }

    try {
      setLoading(true);
      await loginAnonymously();
      navigate('/', { replace: true });
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  return (
    <div className="auth min-h-screen bg-primary flex flex-col">
      <PublicHeader />
      <div className="flex-1 flex items-center justify-center p-4 pt-20">
      <div className="card w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">
            Window Quote Manager
          </h1>
          <p className="text-tertiary">
            {isLogin ? 'Sign in to your account' : 'Create a new account'}
          </p>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-semibold text-primary mb-2">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tertiary w-5 h-5" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-field w-full pl-10 pr-4 py-3 focusable"
                placeholder="Enter your email"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-primary mb-2">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tertiary w-5 h-5" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="input-field w-full pl-10 pr-4 py-3 focusable"
                placeholder="Enter your password"
              />
            </div>
          </div>

          {!isLogin && (
            <>
              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tertiary w-5 h-5" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="input-field w-full pl-10 pr-4 py-3 focusable"
                    placeholder="Confirm your password"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-primary mb-2">
                  Access Code
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tertiary w-5 h-5" />
                  <input
                    type="text"
                    value={accessCode}
                    onChange={(e) => setAccessCode(e.target.value)}
                    required
                    className="input-field w-full pl-10 pr-4 py-3 focusable"
                    placeholder="Enter access code"
                  />
                </div>
                <p className="text-xs text-tertiary mt-1">
                  Contact administrator for access code
                </p>
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`btn btn-primary w-full py-3 ${
              loading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {loading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <>
                {isLogin ? (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    Sign In
                  </>
                ) : (
                  <>
                    <UserPlus className="w-5 h-5 mr-2" />
                    Sign Up
                  </>
                )}
              </>
            )}
          </button>
        </form>

        {/* Anonymous Sign-in Section */}
        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-secondary text-tertiary">or</span>
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-4">
              <label className="block text-sm font-semibold text-primary mb-2">
                Access Code for Anonymous Sign-in
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-tertiary w-5 h-5" />
                <input
                  type="text"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  className="input-field w-full pl-10 pr-4 py-3 focusable"
                  placeholder="Enter access code"
                />
              </div>
              <p className="text-xs text-tertiary mt-1">
                Contact administrator for access code
              </p>
            </div>

            <button
              onClick={handleAnonymousLogin}
              disabled={loading}
              className={`btn btn-secondary w-full py-3 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
              ) : (
                <>
                  <User className="w-5 h-5 mr-2" />
                  Sign In Anonymously
                </>
              )}
            </button>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-tertiary text-sm">
            {isLogin ? "Don't have an account?" : "Already have an account?"}
          </p>
          {isLogin ? (
            <Link to="/signup" className="text-primary font-semibold text-sm hover:underline mt-1 inline-block">
              Sign up here
            </Link>
          ) : (
            <Link to="/signin" className="text-primary font-semibold text-sm hover:underline mt-1 inline-block">
              Sign in here
            </Link>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default Auth;
